// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import type { JsonValue } from './common.types';
import type { IpxRbsValueTransformV1 } from './rbs-value-transform';
import type { IpxDomRpaWorkflowStep } from './samba-rpa-workflow.types';
import type { UrlPatternString } from './backend_config.types';
import {
    IpxWebResourceElementKind,
    type IpxDomRpaWorkflow,
    type IpxWebResourceModelDocument,
    type RpaDomElementAction,
} from './samba-rpa-workflow.types';
import type {
    IpxCredentialRuntimeRoleScope,
    RpaBuilderBootstrapCredentialDefinitionProjection,
    RpaBuilderInvalidLoginWorkflowDefinitionProjection,
    RpaBuilderInvalidWebResourceModelProjection,
    RpaBuilderWebResourceModelProjection,
} from './secret.types';

export const IpxRbsWorkflowClass = {
    ENABLE_PAYMENTS: 'enable_payments',
    TRANSACTION_DETAILS_DETECTION: 'detect_transaction_details',
    ENABLE_RECONCILIATION: 'enable_reconciliation',
    RECONCILE: 'reconcile',
} as const;
export type IpxRbsWorkflowClass = (typeof IpxRbsWorkflowClass)[keyof typeof IpxRbsWorkflowClass];

export const IpxRbsWorkflowWebsiteScopeKind = {
    ALLOWED_WEBSITE_TEMPLATE: 'allowed_website_template',
    ORGANIZATION_ALLOWED_WEBSITE: 'organization_allowed_website',
} as const;
export type IpxRbsWorkflowWebsiteScopeKind =
    (typeof IpxRbsWorkflowWebsiteScopeKind)[keyof typeof IpxRbsWorkflowWebsiteScopeKind];

export const IpxRbsWorkflowDuplicateNameMessage =
    'Workflow name is already used for this website and class.';

export interface IpxRbsWorkflowAllowedWebsiteTemplateScope {
    readonly kind: typeof IpxRbsWorkflowWebsiteScopeKind.ALLOWED_WEBSITE_TEMPLATE;
    readonly allowed_website_template_uuid: string;
}

export interface IpxRbsWorkflowOrganizationAllowedWebsiteScope {
    readonly kind: typeof IpxRbsWorkflowWebsiteScopeKind.ORGANIZATION_ALLOWED_WEBSITE;
    readonly organization_allowed_website_uuid: string;
}

export type IpxRbsWorkflowWebsiteScope =
    | IpxRbsWorkflowAllowedWebsiteTemplateScope
    | IpxRbsWorkflowOrganizationAllowedWebsiteScope;

export type IpxRbsWorkflowRuntimeAllowedWebsiteOwner = IpxRbsWorkflowWebsiteScope;

export const IpxRbsWorkflowBindingDirection = {
    INPUT: 'input',
    OUTPUT: 'output',
} as const;
export type IpxRbsWorkflowBindingDirection =
    (typeof IpxRbsWorkflowBindingDirection)[keyof typeof IpxRbsWorkflowBindingDirection];

export const IpxRbsWorkflowBindingValueKind = {
    BOOLEAN: 'boolean',
    MONEY: 'money',
    STRING: 'string',
    TRANSACTION_DETAILS_FIELD: 'transaction_details_field',
} as const;
export type IpxRbsWorkflowBindingValueKind =
    (typeof IpxRbsWorkflowBindingValueKind)[keyof typeof IpxRbsWorkflowBindingValueKind];

export interface IpxRbsWorkflowBindingDefinition {
    readonly binding_key: string;
    readonly display_name: string;
    readonly direction: IpxRbsWorkflowBindingDirection;
    readonly value_kind: IpxRbsWorkflowBindingValueKind;
    readonly required: boolean;
    readonly allowed_target_kinds: readonly IpxWebResourceElementKind[];
    readonly allowed_actions: readonly RpaDomElementAction[];
}

// The binding shape stored by binding-list versions 1 and 2; frozen.
export interface IpxRbsWorkflowElementBindingV1 {
    readonly binding_key: string;
    readonly element_key: string;
    readonly value_path?: string;
}

// Traceability: implements OBL-RBS-VR-CONTRACT from implementation RBS-BINDING-CURRENT
// Traceability: implements OBL-RBS-WR-CONTRACT from implementation RBS-BINDING-CURRENT
// The current binding. value_transform shapes text read into an output
// binding's field, or a field's value before an input binding writes it.
export interface IpxRbsWorkflowElementBinding {
    readonly binding_key: string;
    readonly element_key: string;
    readonly value_path?: string;
    readonly value_transform?: IpxRbsValueTransformV1;
    // The authored text of a constant input binding (binding_key "constant").
    readonly constant_value?: string;
    // The one step this binding belongs to. Without it, the binding belongs to
    // every step on its element.
    readonly step_id?: string;
}

// Traceability: implements RBS-WORKFLOW-STEP-IDENTITY-001.
// The only way to tell whether a binding belongs to a step.
export function rbsBindingAppliesToStep(
    binding: Pick<IpxRbsWorkflowElementBinding, 'element_key' | 'step_id'>,
    step: Pick<IpxDomRpaWorkflowStep, 'element_key' | 'step_id'>
): boolean {
    return (
        binding.element_key === step.element_key &&
        (binding.step_id === undefined || binding.step_id === step.step_id)
    );
}

export const IpxRbsWorkflowStepBindingSupportedVersions = [1] as const;

// Input binding key whose value is the binding's own constant_value.
export const IpxRbsWorkflowConstantBindingKey = 'constant' as const;

export const IpxRbsWorkflowElementBindingListStorageVersion = {
    V1: 1,
    V2: 2,
    V3: 3,
    V4: 4,
} as const;
export type IpxRbsWorkflowElementBindingListStorageVersion =
    (typeof IpxRbsWorkflowElementBindingListStorageVersion)[keyof typeof IpxRbsWorkflowElementBindingListStorageVersion];

export type IpxRbsWorkflowElementBindingListV1 = readonly IpxRbsWorkflowElementBindingV1[];

export interface IpxRbsWorkflowElementBindingListV2 {
    readonly binding_list_version: typeof IpxRbsWorkflowElementBindingListStorageVersion.V2;
    readonly bindings: readonly IpxRbsWorkflowElementBindingV1[];
}

export interface IpxRbsWorkflowElementBindingListV3 {
    readonly binding_list_version: typeof IpxRbsWorkflowElementBindingListStorageVersion.V3;
    readonly bindings: readonly IpxRbsWorkflowElementBinding[];
}

export interface IpxRbsWorkflowElementBindingListV4 {
    readonly binding_list_version: typeof IpxRbsWorkflowElementBindingListStorageVersion.V4;
    readonly bindings: readonly IpxRbsWorkflowElementBinding[];
}

export type StoredIpxRbsWorkflowElementBindingList =
    | IpxRbsWorkflowElementBindingListV1
    | IpxRbsWorkflowElementBindingListV2
    | IpxRbsWorkflowElementBindingListV3
    | IpxRbsWorkflowElementBindingListV4;

export interface IpxRbsWorkflowEditorPolicy {
    readonly workflow_class: IpxRbsWorkflowClass;
    readonly allowed_target_kinds: readonly IpxWebResourceElementKind[];
    readonly allowed_actions: readonly RpaDomElementAction[];
    readonly input_bindings: readonly IpxRbsWorkflowBindingDefinition[];
    readonly output_bindings: readonly IpxRbsWorkflowBindingDefinition[];
}

const IpxRbsWorkflowReconcileTextActions = [
    'set_text',
    'paste_text',
    'type_text',
] as const satisfies readonly RpaDomElementAction[];

const IpxRbsWorkflowReconcileFieldTargetKinds = [
    IpxWebResourceElementKind.FIELD,
] as const satisfies readonly IpxWebResourceElementKind[];

export const IpxRbsWorkflowEditorPolicies = {
    [IpxRbsWorkflowClass.ENABLE_PAYMENTS]: {
        workflow_class: IpxRbsWorkflowClass.ENABLE_PAYMENTS,
        allowed_target_kinds: [IpxWebResourceElementKind.SCREEN_MARKER],
        allowed_actions: ['wait'],
        input_bindings: [],
        output_bindings: [
            {
                binding_key: 'paymentsEnabled',
                display_name: 'Payments enabled',
                direction: IpxRbsWorkflowBindingDirection.OUTPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.BOOLEAN,
                required: true,
                allowed_target_kinds: [IpxWebResourceElementKind.SCREEN_MARKER],
                allowed_actions: ['wait'],
            },
        ],
    },
    [IpxRbsWorkflowClass.ENABLE_RECONCILIATION]: {
        workflow_class: IpxRbsWorkflowClass.ENABLE_RECONCILIATION,
        allowed_target_kinds: [IpxWebResourceElementKind.SCREEN_MARKER],
        allowed_actions: ['wait'],
        input_bindings: [],
        output_bindings: [
            {
                binding_key: 'reconciliationEnabled',
                display_name: 'Reconciliation enabled',
                direction: IpxRbsWorkflowBindingDirection.OUTPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.BOOLEAN,
                required: true,
                allowed_target_kinds: [IpxWebResourceElementKind.SCREEN_MARKER],
                allowed_actions: ['wait'],
            },
        ],
    },
    // Traceability: implements OBL-RBS-DA-POLICY from implementation RBS-DETECTION-EDITOR-POLICY
    [IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION]: {
        workflow_class: IpxRbsWorkflowClass.TRANSACTION_DETAILS_DETECTION,
        allowed_target_kinds: [
            IpxWebResourceElementKind.SCREEN_MARKER,
            IpxWebResourceElementKind.TEXT,
            IpxWebResourceElementKind.FIELD,
            IpxWebResourceElementKind.BUTTON,
        ],
        allowed_actions: ['wait', 'read', 'click', ...IpxRbsWorkflowReconcileTextActions],
        input_bindings: [
            {
                binding_key: 'transaction_details_field_written',
                display_name: 'Transaction details field read earlier',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.TRANSACTION_DETAILS_FIELD,
                required: false,
                allowed_target_kinds: [IpxWebResourceElementKind.FIELD],
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: IpxRbsWorkflowConstantBindingKey,
                display_name: 'Constant',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: [IpxWebResourceElementKind.FIELD],
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
        ],
        output_bindings: [
            {
                binding_key: 'transaction_details_field',
                display_name: 'Transaction details field',
                direction: IpxRbsWorkflowBindingDirection.OUTPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.TRANSACTION_DETAILS_FIELD,
                required: false,
                allowed_target_kinds: [
                    IpxWebResourceElementKind.TEXT,
                    IpxWebResourceElementKind.FIELD,
                ],
                allowed_actions: ['read'],
            },
        ],
    },
    [IpxRbsWorkflowClass.RECONCILE]: {
        workflow_class: IpxRbsWorkflowClass.RECONCILE,
        allowed_target_kinds: [
            IpxWebResourceElementKind.SCREEN_MARKER,
            IpxWebResourceElementKind.FIELD,
            IpxWebResourceElementKind.BUTTON,
        ],
        allowed_actions: [
            'wait',
            'click',
            'right_click',
            'double_click',
            'set_text',
            'paste_text',
            'type_text',
            'keypress',
        ],
        input_bindings: [
            // Traceability: implements OBL-RBS-WO-CONSTANT from implementation RBS-RECONCILE-CONSTANT-POLICY
            {
                binding_key: IpxRbsWorkflowConstantBindingKey,
                display_name: 'Constant',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: [IpxWebResourceElementKind.FIELD],
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'payment_type_code',
                display_name: 'Payment type code',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: true,
                allowed_target_kinds: [
                    IpxWebResourceElementKind.FIELD,
                    IpxWebResourceElementKind.BUTTON,
                ],
                allowed_actions: [...IpxRbsWorkflowReconcileTextActions, 'click'],
            },
            {
                binding_key: 'amount',
                display_name: 'Amount',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.MONEY,
                required: true,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'detectedAmount',
                display_name: 'Detected amount',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.MONEY,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'tax',
                display_name: 'Tax',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.MONEY,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'fee',
                display_name: 'Fee',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.MONEY,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'surcharge',
                display_name: 'Surcharge',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.MONEY,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'currency',
                display_name: 'Currency',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'merchant_transaction_id',
                display_name: 'Merchant transaction id',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: true,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'customerName',
                display_name: 'Customer name',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'mainPhone',
                display_name: 'Main phone',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'mainEmail',
                display_name: 'Main email',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'description',
                display_name: 'Description',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'notes',
                display_name: 'Notes',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'ipw_customer_id',
                display_name: 'Customer ID',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
            {
                binding_key: 'invoiceId',
                display_name: 'Invoice',
                direction: IpxRbsWorkflowBindingDirection.INPUT,
                value_kind: IpxRbsWorkflowBindingValueKind.STRING,
                required: false,
                allowed_target_kinds: IpxRbsWorkflowReconcileFieldTargetKinds,
                allowed_actions: IpxRbsWorkflowReconcileTextActions,
            },
        ],
        output_bindings: [],
    },
} as const satisfies Record<IpxRbsWorkflowClass, IpxRbsWorkflowEditorPolicy>;

// The transaction-details fields RPA Builder lets a workflow read or write,
// with the labels it shows. Messages operators read name fields by these
// labels, never by binding key.
export interface RbsTransactionDetailsFieldOption {
    readonly bindingKey: string;
    readonly label: string;
}

export const RbsTransactionDetailsFieldOptions: readonly RbsTransactionDetailsFieldOption[] = [
    { bindingKey: 'amount', label: 'Amount' },
    { bindingKey: 'detectedAmount', label: 'Detected amount' },
    { bindingKey: 'tax', label: 'Tax' },
    { bindingKey: 'fee', label: 'Fee' },
    { bindingKey: 'surcharge', label: 'Surcharge' },
    { bindingKey: 'currency', label: 'Currency' },
    { bindingKey: 'customerName', label: 'Customer name' },
    { bindingKey: 'mainPhone', label: 'Main phone' },
    { bindingKey: 'mainEmail', label: 'Main email' },
    { bindingKey: 'description', label: 'Description' },
    { bindingKey: 'notes', label: 'Notes' },
    { bindingKey: 'ipw_customer_id', label: 'Customer ID' },
    { bindingKey: 'invoiceId', label: 'Invoice' },
];

// The label of a workflow binding: a transaction-details field's, else the
// display name an editor policy gives the binding, else a neutral phrase.
export function formatRbsWorkflowBindingLabel(bindingKey: string): string {
    const option = RbsTransactionDetailsFieldOptions.find(
        (candidate) => candidate.bindingKey === bindingKey
    );
    if (option !== undefined) {
        return option.label;
    }
    for (const policy of Object.values(IpxRbsWorkflowEditorPolicies)) {
        const definition: IpxRbsWorkflowBindingDefinition | undefined = [
            ...policy.input_bindings,
            ...policy.output_bindings,
        ].find((candidate) => candidate.binding_key === bindingKey);
        if (definition !== undefined) {
            return definition.display_name;
        }
    }
    return 'A field';
}

export interface IpxRbsWorkflowInstance {
    readonly workflow_instance_uuid: string;
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
    readonly workflow_class: IpxRbsWorkflowClass;
    readonly order: number;
    readonly name: string;
    readonly enabled: boolean;
    readonly ipx_web_resource_model_uuid: string;
    readonly ipx_web_resource_model_revision: string;
    readonly workflow: IpxDomRpaWorkflow;
    readonly input_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly output_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly class_metadata: Record<string, JsonValue>;
}

export interface IpxRbsWorkflowInstanceList {
    readonly workflow_instances: readonly IpxRbsWorkflowInstance[];
}

export interface IpxRbsWorkflowInstanceRuntimeProjection {
    readonly workflow_instance_uuid: string;
    readonly order: number;
    readonly name: string;
    readonly workflow: IpxDomRpaWorkflow;
    readonly input_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly output_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly class_metadata: Record<string, JsonValue>;
}

export interface IpxRbsWorkflowCompositeRuntimeProjection {
    readonly workflow_class: IpxRbsWorkflowClass;
    readonly workflow_id: string;
    readonly page_url_patterns: readonly UrlPatternString[];
    readonly editor_policy: IpxRbsWorkflowEditorPolicy;
    readonly instances: readonly IpxRbsWorkflowInstanceRuntimeProjection[];
}

export interface IpxRbsWorkflowRuntimeProjection {
    readonly projection_id: string;
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
    readonly allowed_website_owner: IpxRbsWorkflowRuntimeAllowedWebsiteOwner;
    readonly allowed_website_url: string;
    readonly ipx_web_resource_model_uuid: string;
    readonly ipx_web_resource_model_revision: string;
    readonly web_resource_model: IpxWebResourceModelDocument;
    readonly composites: readonly IpxRbsWorkflowCompositeRuntimeProjection[];
}

export interface IpxRbsWorkflowRuntimeOpenerTarget {
    readonly name: string;
    readonly allowed_website_owner: IpxRbsWorkflowRuntimeAllowedWebsiteOwner;
    readonly allowed_website_url: string;
    readonly allowed_url_patterns: readonly UrlPatternString[];
}

export interface IpxRbsWorkflowRuntimeBootstrapRequest {
    readonly role_scope: IpxCredentialRuntimeRoleScope;
    // Value-transform versions this Samba client can apply. A client that
    // omits it receives no workflow instance whose bindings carry a transform.
    readonly supported_value_transform_versions?: readonly 1[];
    // A client that omits this is not sent workflows whose steps have ids.
    readonly supported_step_binding_versions?: readonly 1[];
}

export interface IpxRbsWorkflowRuntimeBootstrapResponse {
    readonly role_scope: IpxCredentialRuntimeRoleScope;
    readonly opener_targets: readonly IpxRbsWorkflowRuntimeOpenerTarget[];
    readonly projections: readonly IpxRbsWorkflowRuntimeProjection[];
}

export const RpaBuilderInvalidIpsWorkflowInstanceReason = {
    INVALID_WORKFLOW: 'invalid_workflow',
    INVALID_WORKFLOW_CLASS: 'invalid_workflow_class',
} as const;
export type RpaBuilderInvalidIpsWorkflowInstanceReason =
    (typeof RpaBuilderInvalidIpsWorkflowInstanceReason)[keyof typeof RpaBuilderInvalidIpsWorkflowInstanceReason];

export interface RpaBuilderInvalidIpsWorkflowInstanceProjection {
    readonly invalid_reason: RpaBuilderInvalidIpsWorkflowInstanceReason;
    readonly workflow_instance_uuid: string;
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
    readonly workflow_class: IpxRbsWorkflowClass | string;
    readonly order: number;
    readonly name: string;
    readonly enabled: boolean;
    readonly ipx_web_resource_model_uuid?: string;
    readonly ipx_web_resource_model_revision?: string;
    readonly parse_errors: readonly string[];
}

export type RpaBuilderIpsWorkflowInstanceProjection =
    | IpxRbsWorkflowInstance
    | RpaBuilderInvalidIpsWorkflowInstanceProjection;

export const RpaBuilderConfigurationValidationStatus = {
    VALID: 'valid',
    INVALID: 'invalid',
} as const;
export type RpaBuilderConfigurationValidationStatus =
    (typeof RpaBuilderConfigurationValidationStatus)[keyof typeof RpaBuilderConfigurationValidationStatus];

export const RpaBuilderConfigurationValidationIssueKind = {
    INVALID_WEBSITE_MAP: 'invalid_website_map',
    INVALID_LOGIN_WORKFLOW: 'invalid_login_workflow',
    INVALID_IPS_WORKFLOW: 'invalid_ips_workflow',
} as const;
export type RpaBuilderConfigurationValidationIssueKind =
    (typeof RpaBuilderConfigurationValidationIssueKind)[keyof typeof RpaBuilderConfigurationValidationIssueKind];

export interface RpaBuilderInvalidWebsiteMapConfigurationIssue {
    readonly kind: typeof RpaBuilderConfigurationValidationIssueKind.INVALID_WEBSITE_MAP;
    readonly web_resource_model: RpaBuilderInvalidWebResourceModelProjection;
}

export interface RpaBuilderInvalidLoginWorkflowConfigurationIssue {
    readonly kind: typeof RpaBuilderConfigurationValidationIssueKind.INVALID_LOGIN_WORKFLOW;
    readonly credential_definition: RpaBuilderInvalidLoginWorkflowDefinitionProjection;
}

export interface RpaBuilderInvalidIpsWorkflowConfigurationIssue {
    readonly kind: typeof RpaBuilderConfigurationValidationIssueKind.INVALID_IPS_WORKFLOW;
    readonly workflow_instance: RpaBuilderInvalidIpsWorkflowInstanceProjection;
}

export type RpaBuilderConfigurationValidationIssue =
    | RpaBuilderInvalidWebsiteMapConfigurationIssue
    | RpaBuilderInvalidLoginWorkflowConfigurationIssue
    | RpaBuilderInvalidIpsWorkflowConfigurationIssue;

export const RpaBuilderConfigurationRepairScopeKind = {
    NONE: 'none',
    FULL_CONFIGURATION: 'full_configuration',
    WORKFLOWS: 'workflows',
} as const;
export type RpaBuilderConfigurationRepairScopeKind =
    (typeof RpaBuilderConfigurationRepairScopeKind)[keyof typeof RpaBuilderConfigurationRepairScopeKind];

export interface RpaBuilderNoConfigurationRepairScope {
    readonly kind: typeof RpaBuilderConfigurationRepairScopeKind.NONE;
}

export interface RpaBuilderFullConfigurationRepairScope {
    readonly kind: typeof RpaBuilderConfigurationRepairScopeKind.FULL_CONFIGURATION;
    readonly invalid_website_map: RpaBuilderInvalidWebResourceModelProjection;
}

export interface RpaBuilderWorkflowConfigurationRepairScope {
    readonly kind: typeof RpaBuilderConfigurationRepairScopeKind.WORKFLOWS;
    readonly invalid_login_workflows: readonly RpaBuilderInvalidLoginWorkflowDefinitionProjection[];
    readonly invalid_ips_workflows: readonly RpaBuilderInvalidIpsWorkflowInstanceProjection[];
}

export type RpaBuilderConfigurationRepairScope =
    | RpaBuilderNoConfigurationRepairScope
    | RpaBuilderFullConfigurationRepairScope
    | RpaBuilderWorkflowConfigurationRepairScope;

export interface RpaBuilderValidConfigurationValidationResult {
    readonly status: typeof RpaBuilderConfigurationValidationStatus.VALID;
    readonly issues: readonly RpaBuilderConfigurationValidationIssue[];
    readonly repair_scope: RpaBuilderNoConfigurationRepairScope;
}

export interface RpaBuilderInvalidConfigurationValidationResult {
    readonly status: typeof RpaBuilderConfigurationValidationStatus.INVALID;
    readonly issues: readonly RpaBuilderConfigurationValidationIssue[];
    readonly repair_scope:
        | RpaBuilderFullConfigurationRepairScope
        | RpaBuilderWorkflowConfigurationRepairScope;
}

export type RpaBuilderConfigurationValidationResult =
    | RpaBuilderValidConfigurationValidationResult
    | RpaBuilderInvalidConfigurationValidationResult;

export function isRpaBuilderInvalidIpsWorkflowInstanceProjection(
    projection: RpaBuilderIpsWorkflowInstanceProjection
): projection is RpaBuilderInvalidIpsWorkflowInstanceProjection {
    return 'invalid_reason' in projection;
}

export function isRpaBuilderInvalidWebResourceModelProjection(
    projection: RpaBuilderWebResourceModelProjection | null
): projection is RpaBuilderInvalidWebResourceModelProjection {
    return projection !== null && 'invalid_web_resource_model' in projection;
}

export function isRpaBuilderInvalidLoginWorkflowDefinitionProjection(
    projection: RpaBuilderBootstrapCredentialDefinitionProjection | null
): projection is RpaBuilderInvalidLoginWorkflowDefinitionProjection {
    return projection !== null && 'invalid_reason' in projection;
}

export function deriveRpaBuilderConfigurationValidationResult(input: {
    readonly web_resource_model: RpaBuilderWebResourceModelProjection | null;
    readonly login_workflow_definition: RpaBuilderBootstrapCredentialDefinitionProjection | null;
    readonly ips_workflow_instances: readonly RpaBuilderIpsWorkflowInstanceProjection[];
}): RpaBuilderConfigurationValidationResult {
    const issues: RpaBuilderConfigurationValidationIssue[] = [];
    const invalidWebsiteMap = isRpaBuilderInvalidWebResourceModelProjection(
        input.web_resource_model
    )
        ? input.web_resource_model
        : null;
    const invalidLoginWorkflows = isRpaBuilderInvalidLoginWorkflowDefinitionProjection(
        input.login_workflow_definition
    )
        ? [input.login_workflow_definition]
        : [];
    const invalidIpsWorkflows = input.ips_workflow_instances.filter(
        isRpaBuilderInvalidIpsWorkflowInstanceProjection
    );

    if (invalidWebsiteMap !== null) {
        issues.push({
            kind: RpaBuilderConfigurationValidationIssueKind.INVALID_WEBSITE_MAP,
            web_resource_model: invalidWebsiteMap,
        });
    }
    for (const credentialDefinition of invalidLoginWorkflows) {
        issues.push({
            kind: RpaBuilderConfigurationValidationIssueKind.INVALID_LOGIN_WORKFLOW,
            credential_definition: credentialDefinition,
        });
    }
    for (const workflowInstance of invalidIpsWorkflows) {
        issues.push({
            kind: RpaBuilderConfigurationValidationIssueKind.INVALID_IPS_WORKFLOW,
            workflow_instance: workflowInstance,
        });
    }

    if (invalidWebsiteMap !== null) {
        return {
            status: RpaBuilderConfigurationValidationStatus.INVALID,
            issues,
            repair_scope: {
                kind: RpaBuilderConfigurationRepairScopeKind.FULL_CONFIGURATION,
                invalid_website_map: invalidWebsiteMap,
            },
        };
    }
    if (invalidLoginWorkflows.length > 0 || invalidIpsWorkflows.length > 0) {
        return {
            status: RpaBuilderConfigurationValidationStatus.INVALID,
            issues,
            repair_scope: {
                kind: RpaBuilderConfigurationRepairScopeKind.WORKFLOWS,
                invalid_login_workflows: invalidLoginWorkflows,
                invalid_ips_workflows: invalidIpsWorkflows,
            },
        };
    }

    return {
        status: RpaBuilderConfigurationValidationStatus.VALID,
        issues,
        repair_scope: {
            kind: RpaBuilderConfigurationRepairScopeKind.NONE,
        },
    };
}
